import { money } from "./money";
import type { AdjustmentPolicy } from "./types";

const MICROS_PER_CENT = 10_000;

/**
 * The console's reading of the adjustment policy, before the request is made.
 *
 * Advisory: the API is the gate, and this is the form saying in advance what
 * the API will say. An operator should learn the limit from the dialog they
 * are typing into, not from a 403 after they have typed their password.
 *
 * Returns the sentence to show, or null when the amount may be submitted.
 */
export function adjustmentPrecheck(amountCents: number, policy: AdjustmentPolicy): string | null {
  if (!Number.isFinite(amountCents) || amountCents === 0) return "Enter a non-zero amount.";

  if (policy.youAreMember) {
    return "You're a member of this organization, so another operator has to adjust its credit.";
  }

  const size = Math.abs(amountCents);
  if (size > policy.perAdjustmentCents) {
    return `One adjustment can move at most ${money(policy.perAdjustmentCents * MICROS_PER_CENT)}. Split it, or ask another operator.`;
  }
  if (size > policy.remainingForYouCents) {
    return `That is more than the ${money(policy.remainingForYouCents * MICROS_PER_CENT)} you can still move in the next ${policy.windowHours} hours. Ask another operator for the rest.`;
  }

  return null;
}

/** Cents as the console's money string, for the policy readout. */
export function centsAsMoney(cents: number): string {
  return money(cents * MICROS_PER_CENT);
}

/** What the operator has typed, before any of it is trusted. */
export interface AdjustmentDraft {
  /** The amount as typed, in currency units — "10.00", "-2.5", "". */
  amount: string;
  reason: string;
  password: string;
  code: string;
}

export type AdjustmentFieldErrors = Partial<Record<keyof AdjustmentDraft, string>>;

/** Six digits, which is what an authenticator shows and all the API accepts. */
const CODE_LENGTH = 6;

/**
 * Every field's complaint at once, keyed by the field.
 *
 * One pass over the whole draft rather than the first failure found, so the
 * dialog can mark each field that needs attention beside the field itself. A
 * single sentence at the top saying "a reason is required" leaves the operator
 * to work out which of four boxes it meant; a mark under the box does not.
 *
 * All four fields are required. The amount carries the policy check as well,
 * because an amount the API will refuse is wrong in the same place as an empty
 * one — and for the same reader.
 */
export function adjustmentFieldErrors(
  draft: AdjustmentDraft,
  policy: AdjustmentPolicy,
): AdjustmentFieldErrors {
  const errors: AdjustmentFieldErrors = {};

  if (draft.amount.trim() === "") {
    errors.amount = "Required. Enter an amount.";
  } else {
    const refused = adjustmentPrecheck(Math.round(Number(draft.amount) * 100), policy);
    if (refused) errors.amount = refused;
  }

  if (draft.reason.trim() === "") {
    errors.reason = "Required. The customer reads this on their ledger.";
  }

  if (draft.password === "") {
    errors.password = "Required. Your own password, to confirm it's you.";
  }

  if (draft.code === "") {
    errors.code = "Required. The six digits your authenticator shows now.";
  } else if (draft.code.length !== CODE_LENGTH) {
    errors.code = `The code is ${CODE_LENGTH} digits — ${draft.code.length} typed.`;
  }

  return errors;
}
